import { useState } from 'react';
import { usePolicyDetails } from '../hooks/usePolicyDetails';
import { CollapsibleSection } from './CollapsibleSection';
import { FieldGrid, PerEnvTable } from './policyDetails/FieldGrid';
import { isEmpty } from './policyDetails/format';

interface PolicyDetailsCardProps {
  policy: PolicyFundingSource | null;
}

/**
 * Renders a read-only summary of the policy showing exactly the fields
 * the input form would render for this policy type (see
 * `InsuranceFormFields.tsx`). Construction is delegated to
 * `usePolicyDetails` so this component is purely presentational.
 */
export function PolicyDetailsCard({ policy }: PolicyDetailsCardProps) {
  const [hideEmpty, setHideEmpty] = useState(true);
  const { identityFields, formFields, envRows, hasPerEnv } = usePolicyDetails({ policy });

  const visibleIdentity = hideEmpty ? identityFields.filter(f => !isEmpty(f.value)) : identityFields;
  const visibleFormFields = hideEmpty ? formFields.filter(f => !isEmpty(f.value)) : formFields;

  return (
    <CollapsibleSection title="Policy details" defaultOpen={false}>
      <div className="mb-3 flex items-center justify-end">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={hideEmpty}
            onChange={e => setHideEmpty(e.target.checked)}
            className="h-3 w-3"
          />
          Hide empty fields
        </label>
      </div>
      <div className="space-y-4">
        {visibleIdentity.length > 0 && (
          <section>
            <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
              Identity
            </h3>
            <FieldGrid fields={visibleIdentity} />
          </section>
        )}

        {visibleFormFields.length > 0 && (
          <section>
            <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
              Policy inputs
            </h3>
            <FieldGrid fields={visibleFormFields} />
          </section>
        )}

        {hasPerEnv && (
          <section>
            <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
              Daily benefit by care setting
            </h3>
            <PerEnvTable rows={envRows} />
          </section>
        )}
      </div>
    </CollapsibleSection>
  );
}
